import {
  PosUiActionResult,
  PurchaseClient,
  PurchaseOrderDetailsInput,
  TicketClient,
  TicketGroupInput,
  TicketGroupMergeInput,
} from 'src/WebApiController/index';

import { MAX_PARALLEL_REQUESTS } from './BatchedPurchaseOrderOperator.constants';
import {
  BatchPurchaseProgress,
  BatchPurchaseProgressCallback,
  BatchUpdateResult,
  IBatchedPurchaseOrderOperator,
} from './BatchedPurchaseOrderOperator.types';
import {
  batchEventKeys,
  buildInput,
  filterIntentionsForTicketGroups,
  groupTicketGroupsByEvent,
} from './BatchedPurchaseOrderOperator.utils';

/**
 * Creates and updates purchase orders in batches to avoid performance issues
 * when there are too many ticket groups across many events.
 *
 * **Create flow** (`create` method):
 * 1. Groups ticket groups by `viagogoVirtualId` (event).
 * 2. Batches events so each batch has up to MAX_TICKET_GROUPS_PER_BATCH ticket groups.
 * 3. Calls `insertPurchaseOrder` with the first batch.
 * 4. Iterates over remaining batches calling `addTicketGroupsToPurchaseOrder`.
 *
 * **Update flow** (`update` method):
 * 1. Calls `updatePurchaseOrderByPurchaseOrderId` to update PO-level properties (single call).
 * 2. Groups intentions by event (viagogoVirtualId) and batches by event groups (same as create).
 * 3. Batches run sequentially (not parallel) to avoid SQL deadlocks on listing locks.
 *    The last batch runs alone to ensure PO-level aggregation is calculated correctly.
 *
 * Both flows report progress via the same `BatchPurchaseProgress` callback and support
 * cancellation via `abort`.
 */
export class BatchedPurchaseOrderOperator implements IBatchedPurchaseOrderOperator {
  private onProgress: BatchPurchaseProgressCallback | null = null;
  private cancelled = false;
  private activePurchaseOrderId: number | null = null;
  // Account the in-progress purchase order is being created under (null = current account)
  private activeConsigneeSellerAccountId: string | null = null;
  private abortController: AbortController | null = null;

  constructor(
    private client: PurchaseClient,
    private ticketClient: TicketClient,
    private enabled?: boolean
  ) {}

  /**
   * Aborts the in-progress batch creation/update.
   * - For create: cancels the partially-created purchase order on the server.
   * - For update: aborts in-flight HTTP requests via AbortController.
   * Safe to call when no operation is in progress.
   */
  public async abort(): Promise<void> {
    this.cancelled = true;
    this.abortController?.abort();
    this.abortController = null;

    const poId = this.activePurchaseOrderId;
    if (this.enabled && poId != null) {
      await this.client.cancelPurchaseOrder(
        poId,
        false,
        undefined,
        this.activeConsigneeSellerAccountId
      );
      this.activePurchaseOrderId = null;
    }
  }

  public subscribe = (
    callback: BatchPurchaseProgressCallback
  ): (() => void) => {
    this.onProgress = callback;
    return () => {
      if (this.onProgress === callback) {
        this.onProgress = null;
      }
    };
  };

  /**
   * Processes add-ticket-group batches with at most MAX_PARALLEL_REQUESTS
   * in flight at a time. Returns the first failed result, or null if all succeeded.
   */
  private async runAddQueue(
    batches: string[][],
    groupedByEvent: Map<string, TicketGroupInput[]>,
    input: PurchaseOrderDetailsInput,
    purchaseOrderId: number,
    onBatchDone: (eventCount: number, tgCount: number) => void
  ): Promise<PosUiActionResult | null> {
    let nextIndex = 0;
    let failedResult: PosUiActionResult | null = null;

    const processNext = async (): Promise<void> => {
      while (nextIndex < batches.length && !failedResult && !this.cancelled) {
        const index = nextIndex++;
        const batchKeys = batches[index];
        const batchTGs = batchKeys.flatMap((key) => groupedByEvent.get(key)!);

        const addResult = await this.client.addTicketGroupsToPurchaseOrder(
          buildInput(input, batchTGs, purchaseOrderId),
          true
        );

        if (addResult.entityId == null) {
          failedResult = addResult;
          return;
        }

        onBatchDone(batchKeys.length, batchTGs.length);
      }
    };

    const workers = Array.from(
      { length: Math.min(MAX_PARALLEL_REQUESTS, batches.length) },
      () => processNext()
    );
    await Promise.all(workers);

    return failedResult;
  }

  public create = async (
    input: PurchaseOrderDetailsInput
  ): Promise<PosUiActionResult | null> => {
    this.cancelled = false;
    this.activePurchaseOrderId = null;
    this.activeConsigneeSellerAccountId =
      input.consigneeSellerAccountId ?? null;

    if (!this.enabled) {
      return this.client.insertPurchaseOrder(input, true);
    }

    const groupedByEvent = groupTicketGroupsByEvent(input.ticketGroups);
    const eventKeys = Array.from(groupedByEvent.keys());

    const totalEvents = eventKeys.length;
    const totalTicketGroups = input.ticketGroups.length;

    let processedEvents = 0;
    let processedTicketGroups = 0;

    const emitProgress = (phase: BatchPurchaseProgress['phase']) => {
      this.onProgress?.({
        totalEvents,
        processedEvents,
        totalTicketGroups,
        processedTicketGroups,
        phase,
      });
    };

    // Split event keys into batches of up to MAX_TICKET_GROUPS_PER_BATCH TGs
    const batches = batchEventKeys(eventKeys, groupedByEvent);

    // First batch: insertPurchaseOrder
    const firstBatchKeys = batches[0];
    const firstBatchTGs = firstBatchKeys.flatMap(
      (key) => groupedByEvent.get(key)!
    );

    emitProgress('insert');

    const insertResult = await this.client.insertPurchaseOrder(
      buildInput(input, firstBatchTGs),
      true
    );

    processedEvents += firstBatchKeys.length;
    processedTicketGroups += firstBatchTGs.length;
    emitProgress('insert');

    const purchaseOrderId = insertResult.entityId;
    if (purchaseOrderId == null) {
      return insertResult;
    }

    this.activePurchaseOrderId = purchaseOrderId;

    if (this.cancelled) {
      return insertResult;
    }

    // Remaining batches: addTicketGroupsToPurchaseOrder with concurrency queue
    // Reserve the last batch to run alone so PO-level aggregation is correct
    const remainingBatches = batches.slice(1);
    const parallelBatches = remainingBatches.slice(0, -1);
    const lastBatch = remainingBatches[remainingBatches.length - 1];

    const onBatchDone = (eventCount: number, tgCount: number) => {
      processedEvents += eventCount;
      processedTicketGroups += tgCount;
      emitProgress('add');
    };

    if (parallelBatches.length > 0) {
      const failedResult = await this.runAddQueue(
        parallelBatches,
        groupedByEvent,
        input,
        purchaseOrderId,
        onBatchDone
      );
      if (failedResult) {
        await this.client.cancelPurchaseOrder(
          purchaseOrderId,
          false,
          undefined,
          input.consigneeSellerAccountId
        );
        this.activePurchaseOrderId = null;
        return failedResult;
      }
    }

    // Run the last batch alone to ensure PO-level aggregation is calculated correctly
    if (lastBatch && !this.cancelled) {
      const lastBatchTGs = lastBatch.flatMap((key) => groupedByEvent.get(key)!);
      const lastResult = await this.client.addTicketGroupsToPurchaseOrder(
        buildInput(input, lastBatchTGs, purchaseOrderId),
        true
      );
      if (lastResult.entityId == null) {
        await this.client.cancelPurchaseOrder(
          purchaseOrderId,
          false,
          undefined,
          input.consigneeSellerAccountId
        );
        this.activePurchaseOrderId = null;
        return lastResult;
      }
      onBatchDone(lastBatch.length, lastBatchTGs.length);
    }

    this.activePurchaseOrderId = null;
    return insertResult;
  };

  /**
   * Processes merge batches sequentially. Unlike the create flow (which adds
   * new ticket groups and can run in parallel), merge batches all touch the
   * same existing PO's listings and ticket groups — parallel execution causes
   * SQL deadlocks on listing lock resources.
   *
   * Returns true if any batch failed, false if all succeeded.
   *
   * Batches are partitioned by ticket groups (not intentions) so that ticket
   * groups without a corresponding intention are still included in the correct
   * batch. Intentions for each batch are derived by filtering the full
   * intentions list to the batch's ticket group IDs.
   */
  private runMergeQueue = async (
    batches: string[][],
    groupedByEvent: Map<string, TicketGroupInput[]>,
    baseMergeInput: TicketGroupMergeInput,
    purchaseOrderId: number,
    isManual: boolean,
    signal: AbortSignal,
    onBatchDone: (eventCount: number, tgCount: number) => void
  ): Promise<boolean> => {
    for (const batchKeys of batches) {
      if (this.cancelled) break;

      const batchTGs = batchKeys.flatMap((key) => groupedByEvent.get(key)!);
      const batchIntentions = filterIntentionsForTicketGroups(
        baseMergeInput.intentions,
        batchTGs
      );

      const mergeSuccess =
        await this.ticketClient.mergeTicketGroupsForPurchaseOrderId(
          purchaseOrderId,
          {
            ...baseMergeInput,
            intentions: batchIntentions,
            ticketGroups: batchTGs,
          },
          isManual,
          signal
        );

      if (!mergeSuccess) {
        return true;
      }

      onBatchDone(batchKeys.length, batchTGs.length);
    }

    return false;
  };

  /**
   * Phase 1 of update: calls `updatePurchaseOrderByPurchaseOrderId` once
   * (PO-level props are lightweight — not batched). Emits progress before and
   * after. Returns a failure result if the server returns a non-200 status,
   * or null if the update succeeded or was skipped.
   */
  private updatePOProperties = async (
    input: PurchaseOrderDetailsInput,
    signal: AbortSignal,
    emitProgress: (phase: BatchPurchaseProgress['phase']) => void
  ): Promise<BatchUpdateResult | null> => {
    emitProgress('update');

    const updateResult = await this.client.updatePurchaseOrderByPurchaseOrderId(
      input.id,
      input,
      signal
    );

    if (updateResult.status !== 200) {
      return {
        success: false,
        status: updateResult.status,
        message: updateResult.message ?? null,
      };
    }

    emitProgress('update');
    return null;
  };

  /**
   * Phase 2 (non-batched path): single `mergeTicketGroupsForPurchaseOrderId`
   * call with all intentions. Used when `enabled` is false.
   */
  private mergeUnbatched = async (
    purchaseOrderId: number,
    mergeInput: TicketGroupMergeInput,
    isManual: boolean,
    signal: AbortSignal,
    totalEvents: number,
    totalTicketGroups: number,
    emitProgress: (phase: BatchPurchaseProgress['phase']) => void,
    setProcessed: (events: number, tgs: number) => void
  ): Promise<BatchUpdateResult> => {
    emitProgress('merge');

    const mergeSuccess =
      await this.ticketClient.mergeTicketGroupsForPurchaseOrderId(
        purchaseOrderId,
        mergeInput,
        isManual,
        signal
      );

    setProcessed(totalEvents, totalTicketGroups);
    emitProgress('merge');

    return {
      success: mergeSuccess,
      status: mergeSuccess ? 200 : 500,
      message: mergeSuccess ? null : 'Failed to merge ticket groups',
    };
  };

  /**
   * Phase 2 (batched path): splits ticket-group event keys into batches (same
   * algorithm as create), runs middle batches sequentially via `runMergeQueue`,
   * then runs the last batch alone so PO-level aggregation is calculated
   * correctly. Intentions per batch are derived from the ticket groups in that
   * batch, so ticket groups without a corresponding intention are still
   * included.
   */
  private mergeBatched = async (
    purchaseOrderId: number,
    mergeInput: TicketGroupMergeInput,
    groupedByEvent: Map<string, TicketGroupInput[]>,
    eventKeys: string[],
    isManual: boolean,
    signal: AbortSignal,
    onBatchDone: (eventCount: number, tgCount: number) => void
  ): Promise<BatchUpdateResult> => {
    const batches = batchEventKeys(eventKeys, groupedByEvent);

    // Reserve the last batch to run alone so PO-level aggregation is correct
    const parallelBatches = batches.slice(0, -1);
    const lastBatch = batches[batches.length - 1];

    if (parallelBatches.length > 0) {
      const failed = await this.runMergeQueue(
        parallelBatches,
        groupedByEvent,
        mergeInput,
        purchaseOrderId,
        isManual,
        signal,
        onBatchDone
      );
      if (failed) {
        return {
          success: false,
          status: 500,
          message: 'Failed to merge ticket groups (batched)',
        };
      }
    }

    if (lastBatch && !this.cancelled) {
      const lastBatchTGs = lastBatch.flatMap((key) => groupedByEvent.get(key)!);
      const lastBatchIntentions = filterIntentionsForTicketGroups(
        mergeInput.intentions,
        lastBatchTGs
      );

      const mergeSuccess =
        await this.ticketClient.mergeTicketGroupsForPurchaseOrderId(
          purchaseOrderId,
          {
            ...mergeInput,
            intentions: lastBatchIntentions,
            ticketGroups: lastBatchTGs,
          },
          isManual,
          signal
        );

      if (!mergeSuccess) {
        return {
          success: false,
          status: 500,
          message: 'Failed to merge ticket groups (final batch)',
        };
      }

      onBatchDone(lastBatch.length, lastBatchTGs.length);
    }

    return { success: true, status: 200, message: null };
  };

  /**
   * Updates a purchase order and merges ticket groups in batches.
   *
   * Phase 1 — Update PO-level properties via `updatePurchaseOrderByPurchaseOrderId`
   * (single call, not batched — PO props are lightweight).
   *
   * Phase 2 — Group ticket groups by event (viagogoVirtualId) and batch by
   * event groups (up to MAX_TICKET_GROUPS_PER_BATCH TGs per batch). Batches
   * run sequentially (not parallel) to avoid SQL deadlocks on listing locks.
   * The last batch runs alone to ensure PO-level aggregation is calculated correctly. Intentions
   * for each batch are derived from the batch's ticket group IDs — ticket
   * groups without a corresponding intention are still included in the batch.
   *
   * Progress is reported via the same `BatchPurchaseProgress` callback as
   * create, using `totalTicketGroups`/`processedTicketGroups` for TG counts
   * and `totalEvents`/`processedEvents` for distinct events.
   *
   * When `enabled` is false, falls back to the non-batched single-call approach.
   */
  public update = async (
    input: PurchaseOrderDetailsInput,
    mergeInput: TicketGroupMergeInput,
    options: {
      shouldUpdatePurchaseOrder?: boolean;
      shouldMergeTicketGroups: boolean;
      isManual?: boolean;
    }
  ): Promise<BatchUpdateResult> => {
    this.cancelled = false;
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    const {
      shouldUpdatePurchaseOrder,
      shouldMergeTicketGroups,
      isManual = true,
    } = options;

    // Group and batch by ticket groups — not intentions — so that ticket groups
    // without a corresponding intention (e.g. newly created TGs) are still
    // included in the correct batch. Intentions per batch are derived from
    // the TG subset after partitioning.
    const groupedByEvent = groupTicketGroupsByEvent(mergeInput.ticketGroups);
    const eventKeys = Array.from(groupedByEvent.keys());
    const totalEvents = eventKeys.length;
    const totalTicketGroups = mergeInput.ticketGroups.length;

    let processedEvents = 0;
    let processedTicketGroups = 0;

    const emitProgress = (phase: BatchPurchaseProgress['phase']) => {
      this.onProgress?.({
        totalEvents,
        processedEvents,
        totalTicketGroups,
        processedTicketGroups,
        phase,
      });
    };

    // Phase 1: Update PO-level properties
    if (shouldUpdatePurchaseOrder) {
      const failure = await this.updatePOProperties(
        input,
        signal,
        emitProgress
      );
      if (failure) {
        this.abortController = null;
        return failure;
      }
    }

    if (this.cancelled) {
      this.abortController = null;
      return { success: true, status: 200, message: null };
    }

    // Phase 2: Merge ticket groups
    if (!shouldMergeTicketGroups || mergeInput.ticketGroups.length === 0) {
      this.abortController = null;
      return { success: true, status: 200, message: null };
    }

    const onBatchDone = (eventCount: number, ticketGroupCount: number) => {
      processedEvents += eventCount;
      processedTicketGroups += ticketGroupCount;
      emitProgress('merge');
    };

    const mergeResult = !this.enabled
      ? await this.mergeUnbatched(
          input.id,
          mergeInput,
          isManual,
          signal,
          totalEvents,
          totalTicketGroups,
          emitProgress,
          (events, tgs) => {
            processedEvents = events;
            processedTicketGroups = tgs;
          }
        )
      : await this.mergeBatched(
          input.id,
          mergeInput,
          groupedByEvent,
          eventKeys,
          isManual,
          signal,
          onBatchDone
        );

    this.abortController = null;
    return mergeResult;
  };
}
